import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import styled from "styled-components";

import { getCurrentUser } from "actions/currentUserActions.js";
import { getWarehouses } from "actions/warehouseActions.js";
import { getBuyplanId } from "actions/buyplanIdActions.js";
import { resetOrderDetails } from "actions/orderDetailsActions.js";
import { bulkRemoveItems } from "actions/sparePartActions.js";

import { currentUserDataSelector } from "selectors/currentUserSelector.js";
import { buyplanIdSelector, buyplanStateSelector } from "selectors/buyplanIdSelector.js";
import { activeTabSelector } from "selectors/filtersSelector.js";
import { bannerInfoDataSelector } from "selectors/bannerInfoSelector.js";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";

import ScrollToTop from "components/ScrollToTop.jsx";
import Sidebar from "components/Sidebar/index.jsx";
import TabsList from "components/Tabs/TabsList.jsx";
import SubmitOrderBtn from "components/SubmitOrderBtn.jsx";
import Alert from "components/Alert";

import GlobalMenuContainer from "containers/GlobalMenuContainer";
import ReleaseBannerContainer from "containers/SparePart/ReleaseBannerContainer";
import SubmitOrderContainer from "containers/SubmitOrderContainer.jsx";

import {
  CONSIGNMENT,
  TEMPLATE,
  CONSIGNMENT_REQUEST,
  AGENT_ADMIN
} from "constants.js";
import {
  isStateConsignmentRequest,
  isTemplateStateBuyplan,
  isMarketingRelease
} from "utils.js";

import OperationConfirmationDialog from "containers/OperationConfirmationDialog";
import RangeContainer from "containers/SparePart/RangeContainer.jsx";
import BuyplanDetailsContainer from "containers/SparePart/BuyplanDetailsContainer.jsx";
import {
  buyplanAwaitingApprovalSelector,
  releaseVisibleToRetailersSelector
} from "selectors/buyplanInfoSelector";
import { buyplanDetailsDataSelector } from "selectors/buyplanDetailsSelector.js";
import SparePartOrderReview from "components/SparePart/SparePartOrderReview.jsx";
import DeclineOrderBtn from "components/DeclineOrderBtn";
import DeclineOrderDialog from "components/BuyplanReport/DeclineOrderDialog";
import QuickAddModal from "components/QuickAddModal";
import i18next from "i18next";

const detailTabName = (buyplanState, tabsConfigLoclisationData) => {
  switch (buyplanState) {
    case CONSIGNMENT:
      return tabsConfigLoclisationData.consignment;
    case TEMPLATE:
      return tabsConfigLoclisationData.template;
    case CONSIGNMENT_REQUEST:
      return tabsConfigLoclisationData.consignment;
    default:
      return tabsConfigLoclisationData.buyPlan;
  }
};

// Spare Part tabs config - ONLY 2 tabs: Products and Buyplan Details
const tabsConfig = (buyplanState, tabsConfigLoclisationData) => {
  return {
    range: {
      id: "RANGE_PAGE_TAB",
      title: tabsConfigLoclisationData.range,
      body: <RangeContainer />
    },
    buyplanDetails: {
      id: "BUYPLAN_DETAILS_TAB",
      title: detailTabName(buyplanState, tabsConfigLoclisationData),
      body: <BuyplanDetailsContainer />
    }
  };
};

const getTabsConfig = (
  isAdminUser,
  isAgentUser,
  isSalesRep,
  isSalesStaff,
  isSuperSupplierAdmin,
  isSupplierAdmin,
  forBuyplan,
  buyplanState,
  tabsConfigLoclisationData
) => {
  let pageTabsConfig = tabsConfig(buyplanState, tabsConfigLoclisationData);

  if (
    (isAgentUser ||
      isAdminUser ||
      isSuperSupplierAdmin ||
      isSalesRep ||
      isSalesStaff ||
      isSupplierAdmin) &&
    !forBuyplan
  ) {
    delete pageTabsConfig["buyplanDetails"];
    return { ...pageTabsConfig };
  }

  return { ...pageTabsConfig };
};

const SparePartLayout = ({ className }) => {
  const [isModalOpen, setModalState] = useState(false);
  const dispatch = useDispatch();
  const toggleModalFn = useCallback(() => {
    dispatch(resetOrderDetails());
    setModalState(prev => !prev);
  }, [dispatch]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const buyplanType = queryParams.get("type");

  const currentUser = useSelector(currentUserDataSelector);
  const buyplanState = useSelector(buyplanStateSelector);
  const confirmedBuyplanId = useSelector(buyplanIdSelector);
  const activeTab = useSelector(activeTabSelector);
  const bannerInfoData = useSelector(bannerInfoDataSelector);
  const isAwaitingApproval = useSelector(buyplanAwaitingApprovalSelector);
  const isReleaseVisibleToRetailer = useSelector(releaseVisibleToRetailersSelector);
  const buyplanDetails = useSelector(buyplanDetailsDataSelector);
  const isStateConsignment = buyplanType === CONSIGNMENT;
  const isAdminUser = currentUser && currentUser.get("role") === "Brandscope Admin";
  const isAgentUser = currentUser && currentUser.get("role") === AGENT_ADMIN;
  const isSalesRep = currentUser && currentUser.get("role") === "Salesrep";
  const isRetailerEmployee = currentUser && currentUser.get("role") === "Retailer Employee";
  const isSupplierAdmin = currentUser && currentUser.get("role") === "Supplier Admin";
  const isSalesStaff = currentUser && currentUser.get("role") === "Sales Staff";
  const isSuperSupplierAdmin = currentUser && currentUser.get("role") === "Super Supplier Admin";

  const { buyplanId, releaseId } = useParams();
  const forBuyplan = buyplanId ? true : false;
  const releaseStatus = bannerInfoData.get("release_status");
  const isMarketingrelease = isMarketingRelease(releaseStatus);
  const isBpTemplate = isTemplateStateBuyplan(buyplanState);

  const [isDeclineModalOpen, setDeclineModalOpen] = useState(false);
  const warehouseId = useSelector(warehouseIdSelector);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isQuickAddModalOpen, setQuickAddModalOpen] = useState(false);

  const tabsConfigLoclisationData = {
    range: i18next.t("tabsConfig.product"),
    consignment: i18next.t("tabsConfig.detailTabName.consignment"),
    template: i18next.t("tabsConfig.detailTabName.template"),
    buyPlan: i18next.t("tabsConfig.buyPlan")
  };

  const toggleDeclineModel = () => {
    setDeclineModalOpen(prev => !prev);
  };

  const handleBulkRemove = () => {
    if (!confirmedBuyplanId || !releaseId) return;
    setDeleteConfirmOpen(false);

    const filters = {
      common_filters: {
        only_buy_plan_items: null,
        exclude_sold_out_items: null,
        min_price: null,
        max_price: null,
        currency: null,
        currency_symbol: null,
        hide_whole_sale_price: null
      },
      dynamic_filters: {
        divisions: [],
        collections: [],
        filter_tags: [],
        sizes: [],
        delivery_months: [],
        genders: [],
        age_groups: [],
        colours: []
      }
    };

    dispatch(bulkRemoveItems(releaseId, confirmedBuyplanId, warehouseId, filters));
  };

  const showSubmitBtn = () => {
    if (isRetailerEmployee || isAdminUser || isBpTemplate || isStateConsignmentRequest(buyplanState))
      return false;
    if (isAgentUser && isMarketingrelease) return false;
    return true;
  };

  const showDeclineOrderBtn = () => {
    return (isAgentUser || isSalesRep || isSuperSupplierAdmin) && isAwaitingApproval;
  };

  const submitHeader = isStateConsignment
    ? i18next.t("buttons.submitRequest")
    : showDeclineOrderBtn()
    ? i18next.t("buttons.approveOrder")
    : i18next.t("buttons.submitOrder");

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (forBuyplan) {
      dispatch(getBuyplanId({ releaseId, buyplan_id: buyplanId, type: buyplanType }));
    } else {
      dispatch(getBuyplanId({ releaseId, type: buyplanType }));
    }
  }, [dispatch, forBuyplan, releaseId, buyplanId, buyplanType]);

  useEffect(() => {
    if (
      (isAdminUser || isSalesRep || isSuperSupplierAdmin || isSalesStaff || isSupplierAdmin || isAgentUser) &&
      !forBuyplan
    ) {
      dispatch(getWarehouses({ buyplanId: null, release_id: releaseId }));
    } else if (confirmedBuyplanId) {
      dispatch(getWarehouses({ buyplanId: confirmedBuyplanId }));
    }
  }, [
    dispatch,
    isAdminUser,
    isSalesRep,
    isSuperSupplierAdmin,
    isSalesStaff,
    isSupplierAdmin,
    isAgentUser,
    forBuyplan,
    releaseId,
    confirmedBuyplanId
  ]);

  if (
    !confirmedBuyplanId &&
    !isAdminUser &&
    !isAgentUser &&
    !isSuperSupplierAdmin &&
    !isSalesRep &&
    !isSalesStaff &&
    !isSupplierAdmin
  ) {
    return null;
  }

  const showVisibilityWarningBanner =
    (isAgentUser || isSalesRep || isSuperSupplierAdmin) && !isReleaseVisibleToRetailer;

  return (
    <>
      <GlobalMenuContainer />

      <div className={`${className}`}>
        <Sidebar />

        <main className={`main ${activeTab === "report" ? "w-100" : ""}`}>
          {showVisibilityWarningBanner && (
            <Alert variant="info">{i18next.t("retailerVisibility.info")}</Alert>
          )}

          <ReleaseBannerContainer hideBuyplanInfo={true} />

          {buyplanDetails && !isAdminUser && (
            <SparePartOrderReview
              totalPriceFormatted={
                buyplanDetails && buyplanDetails.getIn(["buyplan", "total_price_formatted"])
              }
              totalItemsCount={
                buyplanDetails && buyplanDetails.getIn(["buyplan", "total_items_count"])
              }
            />
          )}

          <section>
            <div
                className="btn-wrap float-right pl-3"
                style={{ height: "3.45rem" }}
              >
              {(
                <button
                  type="button"
                  className="btn btn-primary sp-icon-btn"
                  onClick={() => setQuickAddModalOpen(true)}
                  title="Quick Add"
                >
                  <i className="fa fa-search" />
                </button>
              )}

              {showDeclineOrderBtn() && (
                <DeclineOrderBtn
                  type="button"
                  className="btn btn-danger"
                  onClick={toggleDeclineModel}
                >
                  {i18next.t("buttons.declineOrder")}
                </DeclineOrderBtn>
              )}

              {showSubmitBtn() && (
                <SubmitOrderBtn
                  type="button"
                  className="btn btn-success"
                  onClick={toggleModalFn}
                >
                  {submitHeader}
                </SubmitOrderBtn>
              )}

              {confirmedBuyplanId && (
                <button
                  type="button"
                  className="btn btn-danger sp-icon-btn"
                  onClick={() => setDeleteConfirmOpen(true)}
                  title={i18next.t("buttons.removeAll")}
                >
                  <i className="fa fa-trash" />
                </button>
              )}
            </div>

            <OperationConfirmationDialog
              isOpen={isDeleteConfirmOpen}
              message={i18next.t("removeAllBuyplanItems.confirmText", "Remove all items from this buyplan?")}
              onCloseCallback={() => setDeleteConfirmOpen(false)}
              onAcceptCallback={handleBulkRemove}
              onRejectCallback={() => setDeleteConfirmOpen(false)}
            />

            <TabsList
              tabsConfig={getTabsConfig(
                isAdminUser,
                isAgentUser,
                isSalesRep,
                isSalesStaff,
                isSuperSupplierAdmin,
                isSupplierAdmin,
                forBuyplan,
                buyplanState,
                tabsConfigLoclisationData
              )}
            />
          </section>

          {!isAdminUser && (
            <SubmitOrderContainer isOpen={isModalOpen} closeModalCallback={toggleModalFn} />
          )}

          {confirmedBuyplanId && showDeclineOrderBtn() && (
            <DeclineOrderDialog
              isOpen={isDeclineModalOpen}
              closeModalCallback={toggleDeclineModel}
              buyplanId={confirmedBuyplanId}
            />
          )}

          <QuickAddModal
            isOpen={isQuickAddModalOpen}
            closeModal={() => setQuickAddModalOpen(false)}
            releaseId={releaseId}
            buyplanId={confirmedBuyplanId}
          />
        </main>

        <ScrollToTop />
      </div>
    </>
  );
};

const StyledSparePartLayout = styled(SparePartLayout)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 20px 15px 0;

  @media (max-width: 575.98px) {
    flex-direction: column;
  }

  .main {
    width: var(--main-width);
    padding-left: 15px;

    @media (max-width: 575.98px) {
      width: 100%;
      padding-left: 0;
    }
  }

  .btn-wrap {
    padding: 11.5px 0;
    border-top: 1px solid #dddddd;
    border-bottom: 1px solid #dddddd;
    display: flex;
    align-items: center;
    gap: 6px;

    /* Zero out all margins so only gap controls spacing */
    > * {
      margin: 0 !important;
    }

    .sp-icon-btn {
      align-self: stretch;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 12px;
    }

    .btn-danger {
      background-color: #dc3545 !important;
      border-color: #dc3545 !important;
      color: #fff !important;

      &:hover,
      &:focus,
      &:active {
        background-color: #c82333 !important;
        border-color: #bd2130 !important;
        color: #fff !important;
      }
    }

    @media (max-width: 575.98px) {
      float: none !important;
      margin-bottom: -1px;
      text-align: right;
      flex-wrap: wrap;
    }
  }

  .release-banner {
    margin: 0 0 10px;
  }
`;

export default React.memo(StyledSparePartLayout);
